import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Download, FileSpreadsheet, Search, Upload } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import * as api from '../../api';
import { Btn, Input, Modal, RNLLoader, Select } from '../UI';
import Pagination from '../requisition/Pagination';
import { CommentModal, Pill } from '../materialTakeoff/MTOShared';
import { exportXlsx, fmtDate } from '../materialMapping/mmUtils';

const STATUS = {
  reserved: { label: 'Free', color: '#0369a1', bg: '#e0f2fe' },
  used: { label: 'On BOQ', color: '#166534', bg: '#dcfce7' },
  void: { label: 'Void', color: '#b91c1c', bg: '#fee2e2' },
};
const th = { padding: '10px 12px', fontSize: 10, fontWeight: 800, color: '#fff', background: '#0b318bff', border: '1px solid black',
             textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 };
const td = { padding: '8px 12px', fontSize: 12.5, borderBottom: '1px solid var(--border-light)', verticalAlign: 'top' };
const small = { padding: '4px 10px', fontSize: 11.5 };

const EXPORT_COLS = [
  { key: 'code', label: 'BOQ CODE' }, { key: 'description', label: 'Description' }, { key: 'discipline_code', label: 'Discipline' },
  { key: 'parent_code', label: 'Main Code' }, { key: 'status', label: 'Status', value: r => STATUS[r.status]?.label || r.status },
  { key: 'boq_description', label: 'BOQ Line' }, { key: 'building', label: 'Building' }, { key: 'previous_code', label: 'Previous Code' },
  { key: 'generated_by', label: 'Issued By' }, { key: 'generated_at', label: 'Issued On', value: r => fmtDate(r.generated_at) },
  { key: 'void_reason', label: 'Void Reason' },
];

// Every code issued for the project.
export default function CGRegister({ projectId, project, disciplines, version, onChanged, canEdit, toast }) {
  const [searchParams] = useSearchParams();
  const [f, setF] = useState({ discipline: searchParams.get('discipline') || '', status: searchParams.get('status') || '', kind: '', search: '' });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState({ skip: 0, limit: 50 });
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [voiding, setVoiding] = useState(null);
  const [editing, setEditing] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [busy, setBusy] = useState('');
  const fileRef = useRef(null);

  const params = useCallback((extra) => ({ project_id: projectId, discipline: f.discipline, status: f.status, kind: f.kind, search: query, ...extra }),
    [projectId, f.discipline, f.status, f.kind, query]);

  const load = useCallback(() => {
    setRows(null);
    api.listBoqCodes(params({ skip: page.skip, limit: page.limit }))
      .then(r => { setRows(r || []); setTotal(r?.totalCount ?? (r || []).length); })
      .catch(e => { setRows([]); toast?.show?.(e.message || 'Could not load the register', 'error'); });
  }, [params, page, toast]);
  useEffect(() => { load(); }, [load, version]);

  const set = (k) => (v) => { setF(p => ({ ...p, [k]: v })); setPage(p => ({ ...p, skip: 0 })); };
  const act = async (fn, ok) => {
    try { await fn(); api.clearApiCache(); toast?.show?.(ok, 'success'); onChanged(); }
    catch (e) { toast?.show?.(e.message || 'Something went wrong', 'error'); }
  };

  const doExport = async () => {
    setBusy('export');
    try { exportXlsx(await api.listBoqCodes(params({ skip: 0, limit: 10000 })) || [], EXPORT_COLS, `BOQ-Code-Register-${project?.project_code || ''}`, 'Codes'); }
    catch (e) { toast?.show?.(e.message || 'Export failed', 'error'); }
    setBusy('');
  };

  const template = () => exportXlsx([
    { code: 'RDV-ELECT-0001', description: 'Example main code — replace RDV with this project’s prefix' },
    { code: 'RDV-ELECT-0001-01', description: 'Example sub-code (its main code must be in the file or the register)' },
  ], [{ key: 'code', label: 'BOQ CODE' }, { key: 'description', label: 'Description' }], 'BOQ-Code-Import-Template', 'Codes');

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy('import');
    try {
      const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      const key = (row, names) => Object.keys(row).find(k => names.includes(k.trim().toLowerCase().replace(/[^a-z]/g, '')));
      const rowsIn = data.map(r => ({ code: String(r[key(r, ['boqcode', 'code'])] ?? '').trim(), description: String(r[key(r, ['description', 'desc'])] ?? '').trim() || null }))
        .filter(r => r.code);
      if (!rowsIn.length) throw new Error('No codes found — the sheet needs a "BOQ CODE" column.');
      const res = await api.importBoqCodes({ project_id: projectId, rows: rowsIn });
      setImportResult(res);
      onChanged();
    } catch (err) {
      toast?.show?.(err.message || 'Import failed', 'error');
    }
    setBusy('');
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Code Register</h3>
          <p style={{ margin: '3px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
            Every code issued for this project. Free codes are reserved for use; codes on a BOQ line show the line; void codes are never re-issued.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn variant="secondary" onClick={doExport} loading={busy === 'export'}><Download size={14} /> Export Excel</Btn>
          {canEdit && (
            <>
              <Btn variant="secondary" onClick={template}><FileSpreadsheet size={14} /> Download Template</Btn>
              <Btn variant="secondary" loading={busy === 'import'} onClick={() => fileRef.current?.click()}><Upload size={14} /> Import from Excel</Btn>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={onFile} />
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
        <Select label="Discipline" value={f.discipline} onChange={set('discipline')}
                options={[{ value: '', label: 'All disciplines' }, ...disciplines.map(d => ({ value: d.code, label: `${d.code} — ${d.name}` }))]} />
        <Select label="Status" value={f.status} onChange={set('status')}
                options={[{ value: '', label: 'All' }, ...Object.entries(STATUS).map(([value, s]) => ({ value, label: s.label }))]} />
        <Select label="Kind" value={f.kind} onChange={set('kind')}
                options={[{ value: '', label: 'Main and sub-codes' }, { value: 'main', label: 'Main codes' }, { value: 'sub', label: 'Sub-codes' }]} />
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <Input value={f.search} onChange={v => setF(p => ({ ...p, search: v }))} placeholder="Search code, description, previous code or BOQ line…"
                 onKeyDown={e => { if (e.key === 'Enter') { setQuery(f.search.trim()); setPage(p => ({ ...p, skip: 0 })); } }} />
        </div>
        <Btn onClick={() => { setQuery(f.search.trim()); setPage(p => ({ ...p, skip: 0 })); }}><Search size={14} /> Search</Btn>
      </div>

      {rows === null ? <RNLLoader title="Loading register..." /> : (
        <>
          <div style={{ overflowX: 'auto', border: '1px solid black', borderRadius: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['BOQ Code', 'Disc.', 'Description', 'Status', 'BOQ Line', 'Previous Code', 'Issued', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', padding: 30, color: 'var(--text-secondary)' }}>No codes yet — issue some in Generate Codes.</td></tr>}
                {rows.map(r => (
                  <tr key={r.id} style={{ opacity: r.status === 'void' ? 0.65 : 1 }}>
                    <td style={{ ...td, fontFamily: 'monospace', fontWeight: 800, color: '#0b318b', whiteSpace: 'nowrap', paddingLeft: r.parent_id ? 28 : 12 }}>
                      {r.parent_id && '↳ '}{r.code}
                      {r.sub_count > 0 && <div style={{ fontSize: 10.5, fontFamily: 'var(--font)', fontWeight: 600, color: 'var(--text-secondary)' }}>{r.sub_count} sub-code{r.sub_count === 1 ? '' : 's'}</div>}
                    </td>
                    <td style={td}>{r.discipline_code}</td>
                    <td style={{ ...td, minWidth: 180 }}>{r.description || '—'}</td>
                    <td style={td}>
                      <Pill meta={STATUS[r.status]} />
                      {r.void_reason && <div style={{ fontSize: 11, marginTop: 3 }}>{r.void_reason}</div>}
                    </td>
                    <td style={{ ...td, minWidth: 200, maxWidth: 360 }}>
                      {r.boq_description ? <>{r.boq_description}{r.building && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.building}</div>}</> : '—'}
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{r.previous_code || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(r.generated_at)}<div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{r.generated_by || ''}</div></td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {canEdit && (
                        <div style={{ display: 'flex', gap: 5 }}>
                          <Btn style={small} variant="secondary" onClick={() => setEditing({ id: r.id, code: r.code, description: r.description || '' })}>Edit</Btn>
                          {r.status === 'reserved' && <Btn style={small} variant="ghost" onClick={() => setVoiding(r)}>Void</Btn>}
                          {r.status === 'void' && <Btn style={small} variant="secondary" onClick={() => act(() => api.restoreBoqCode(r.id), `${r.code} restored`)}>Restore</Btn>}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 12 }}><Pagination total={total} skip={page.skip} limit={page.limit} onChange={setPage} /></div>
        </>
      )}

      {voiding && (
        <CommentModal title={`Void ${voiding.code}`} message="A void code is never issued again. You can restore it later if it was voided by mistake."
                      label="Reason" required danger confirmLabel="Void"
                      onClose={() => setVoiding(null)}
                      onConfirm={async (reason) => { await act(() => api.voidBoqCode(voiding.id, reason), `${voiding.code} voided`); setVoiding(null); }} />
      )}

      {editing && (
        <Modal title={`Edit ${editing.code}`} onClose={() => setEditing(null)} width={520} blur={2}>
          <Input label="Description" value={editing.description} onChange={v => setEditing(p => ({ ...p, description: v }))} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <Btn variant="secondary" onClick={() => setEditing(null)}>Cancel</Btn>
            <Btn onClick={async () => { await act(() => api.updateBoqCode(editing.id, { description: editing.description }), 'Saved'); setEditing(null); }}>Save</Btn>
          </div>
        </Modal>
      )}

      {importResult && (
        <Modal title="Import result" onClose={() => setImportResult(null)} width={620} blur={2}>
          <p style={{ margin: '0 0 10px', fontSize: 14 }}><b>{importResult.added}</b> code(s) added to the register.</p>
          {importResult.skipped.length > 0 && (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{importResult.skipped.length} skipped:</div>
              <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: 8 }}>
                {importResult.skipped.map((s, i) => (
                  <div key={i} style={{ padding: '6px 10px', fontSize: 12.5, borderBottom: '1px solid var(--border-light)' }}>
                    <b style={{ fontFamily: 'monospace' }}>{s.code}</b> — {s.reason}
                  </div>
                ))}
              </div>
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}><Btn onClick={() => setImportResult(null)}>Close</Btn></div>
        </Modal>
      )}
    </div>
  );
}
